/*
  Окно «Какие счета загрузить» - решение владельца 16.10.2026: вариант А
  (одним списком) с переходом в вариант Б (по домам). 19.10.2026 владелец
  добавил третью вкладку - «По листам», после «По домам»: файл читается всеми
  листами, и выбирать хочется листом целиком.

  Открывается отдельной кнопкой рядом с записью и одно на все загрузки, где в
  файле есть номера счетов. Пока окно не открывали, загружается весь файл, как
  раньше. «Применить» возвращает перечень номеров; отмечено всё - возвращает
  пусто, то есть «весь файл»: так у загрузки нет второго пути для того же
  случая.

  Отбор по листу до 19.10.2026 стоял кнопками в переключателе рядом с
  «Требуют внимания»; вкладка его заменила, а не повторила: одно действие -
  одно место, и имена файлов вкладки «Файлы» в переключатель не помещаются.
*/

import { Field, Switch } from '../Common.jsx'
import Modal from '../Modal.jsx'
import { differing } from './NameDifferences.jsx'
import PickerGroups from './PickerGroups.jsx'
import PickerTable from './PickerTable.jsx'
import usePicker, { ALL, ATTENTION, DIFFERS, HOUSES, SHEETS } from './usePicker.js'

// Вкладка листов - только у файла, где листов больше одного: сервер называет
// листы лишь тогда (`account_choice.AccountChoice.sheets`).
function modes(choices) {
  const sheets = new Set(choices.flatMap((choice) => (choice.sheets ?? []).map((sheet) => sheet.key)))
  return [
    { value: 'list', title: 'Списком' },
    { value: HOUSES, title: 'По домам' },
    ...(sheets.size > 1 ? [{ value: SHEETS, title: 'По листам' }] : []),
  ]
}

function views(choices) {
  const attention = choices.filter((choice) => choice.attention).length
  const differs = differing(choices)
  return [
    { value: ALL, title: `Все ${choices.length}` },
    ...(attention ? [{ value: ATTENTION, title: `Требуют внимания ${attention}` }] : []),
    ...(differs ? [{ value: DIFFERS, title: `ФИО расходятся ${differs}` }] : []),
  ]
}

// Счёт выбирается целиком, а не по листу: снятый на одном листе, он снят и на
// остальных. Без этой строки галочка «частично» у соседнего листа выглядела бы
// сбоем.
function SheetsNote({ choices }) {
  if (!choices.some((choice) => (choice.sheets ?? []).length > 1)) return null
  return (
    <p className="muted picker-note">
      Счёт, который есть на нескольких листах, отмечается и снимается сразу на всех: загружаются
      счета со всеми их сведениями, а не отдельные листы.
    </p>
  )
}

export default function AccountPicker({ choices, initial, note, amountTitle, onApply, onClose }) {
  const state = usePicker(choices, initial)
  const { picked, shown, mark } = state

  function apply() {
    const numbers = choices.map((choice) => choice.number).filter((number) => picked.has(number))
    onApply(numbers.length === choices.length ? null : numbers)
  }

  // Всё, от чего зависит показанный список: его смена листает таблицу к началу (23.10.2026).
  const filterKey = [state.mode, state.view, state.current?.key ?? '', state.typed.trim()].join('|')
  const table = (
    <PickerTable
      shown={shown}
      picked={picked}
      mark={mark}
      amountTitle={amountTitle}
      filterKey={filterKey}
    />
  )
  return (
    <Modal title="Какие счета загрузить" note={note} onClose={onClose} wide>
      <div className="row picker-tools">
        <Switch value={state.mode} onChange={state.setMode} options={modes(choices)} />
        <Switch value={state.view} onChange={state.setView} options={views(choices)} />
        <Field label="Поиск" width="l">
          <input
            className="field__input"
            type="search"
            placeholder="номер счёта, фамилия или адрес"
            value={state.typed}
            onChange={(event) => state.setTyped(event.target.value)}
          />
        </Field>
      </div>
      {state.mode === SHEETS ? <SheetsNote choices={choices} /> : null}

      {state.groups ? (
        <div className="picker-split">
          <PickerGroups
            groups={state.groups}
            label={state.mode === SHEETS ? 'Листы' : 'Дома'}
            current={state.current}
            setGroup={state.setGroup}
            picked={picked}
            mark={mark}
          />
          {table}
        </div>
      ) : (
        table
      )}

      <div className="modal__foot">
        <b>
          Отмечено: {picked.size} из {choices.length}
          {shown.length < choices.length ? ` · показано ${shown.length}` : ''}
        </b>
        <span className="modal__foot-spacer" />
        <button type="button" className="button button--quiet" onClick={() => mark(shown, true)}>
          Отметить показанные
        </button>
        <button type="button" className="button button--quiet" onClick={() => mark(shown, false)}>
          Снять показанные
        </button>
        <button type="button" className="button" disabled={picked.size === 0} onClick={apply}>
          Применить
        </button>
        <button type="button" className="button button--quiet" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  )
}
